import React, { Component } from "react";
import { Box, CircularProgress, Grid, Typography } from "@material-ui/core";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import HeaderInfo from "../../waybee-ui/HeaderInfo/HeaderInfo";
import Group from "../../waybee-ui/Group";
import CollapseGroup from "../../waybee-ui/CollapseGroup";
import Calendar from "../../waybee-ui/Calendar/Calendar";
import WuiClassesList from "../Classes/components/ClassesList";
import TeacherService from "../../services/TeacherService";

const ClassesList = styled(WuiClassesList)`
  margin: 0;
  width: 100%;
`;

const LoadContainer = styled.div`
  text-align: center;
  margin: 24px;
`;

class TeacherDetail extends Component {
  constructor(props) {
    super(props);

    this.state = {
      teacher: null,
      classes: [],
      accessibleClassIds: [],
      loading: true,
      loadError: false,
      calendarOptions: null,
      scheduleEmpty: false,
      scheduleError: false,
      scheduleLoading: false
    };
  }

  componentDidMount() {
    this.loadTeacher();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (prevProps.match.params.id !== match.params.id) {
      this.loadTeacher();
    }
  }

  loadTeacher = async () => {
    const { match, alert, t } = this.props;
    const teacherId = match.params.id;

    this.setState({
      loading: true,
      loadError: false,
      scheduleEmpty: false,
      scheduleError: false
    });
    try {
      const teacher = await TeacherService.getTeacher(teacherId);

      const [teacherClassesResult, loggedUserClassesResult] = await Promise.all(
        [
          teacher.CODPROF
            ? TeacherService.getClassesForTeacher(teacher.CODPROF).catch(() => {
                alert.show(t("teachers.details.errorClasses"), {
                  title: t("common.actions.error")
                });
                return [];
              })
            : Promise.resolve([]),
          TeacherService.getClasses().catch(() => [])
        ]
      );

      const loggedUserClasses = loggedUserClassesResult || [];
      this.setState({
        teacher,
        classes: teacherClassesResult || [],
        accessibleClassIds: loggedUserClasses.map(classe => classe.id),
        calendarOptions: this.buildCalendarOptions(teacher),
        loading: false
      });
    } catch (e) {
      alert.show(t("teachers.details.errorFetch"), {
        title: t("common.actions.error")
      });
      this.setState({
        teacher: null,
        classes: [],
        accessibleClassIds: [],
        loading: false,
        loadError: true,
        calendarOptions: null
      });
    }
  };

  buildCalendarOptions = teacher => {
    const teacherId = teacher.CODIGO || teacher.CODPROF || teacher.id;
    return {
      views: {
        timeGridWeek: {
          titleFormat: { month: "long", day: "numeric" },
          slotLabelFormat: { hour: "numeric", minute: "2-digit" },
          columnHeaderFormat: { weekday: "short", day: "numeric" }
        },
        timeGridDay: {
          titleFormat: { month: "long", day: "numeric", weekday: "short" }
        }
      },
      allDaySlot: false,
      events: async (info, successCallback) => {
        this.setState({
          scheduleLoading: true,
          scheduleEmpty: false,
          scheduleError: false
        });
        try {
          const events = await TeacherService.getSchedule(
            info.startStr,
            info.endStr,
            teacherId
          );
          const list = events || [];
          this.setState({
            scheduleEmpty: list.length === 0,
            scheduleError: false,
            scheduleLoading: false
          });
          successCallback(list);
        } catch (e) {
          this.setState({
            scheduleEmpty: false,
            scheduleError: true,
            scheduleLoading: false
          });
          successCallback([]);
        }
      },
      defaultView: "timeGridWeek"
    };
  };

  render() {
    const {
      teacher,
      classes,
      accessibleClassIds,
      loading,
      loadError,
      calendarOptions,
      scheduleEmpty,
      scheduleError,
      scheduleLoading
    } = this.state;
    const { t } = this.props;

    if (loading) {
      return (
        <LoadContainer>
          <CircularProgress />
        </LoadContainer>
      );
    }

    if (loadError || !teacher) {
      return (
        <Box p={4} textAlign="center">
          <Typography color="textSecondary">
            {t("teachers.details.notFound")}
          </Typography>
        </Box>
      );
    }

    return (
      <div>
        <Grid container>
          <Grid item xs={12}>
            <Group>
              <HeaderInfo
                name={teacher.NOME}
                info={teacher.EMAIL || ""}
                tag={teacher.SDISCIPLINA_NOME || teacher.FUNCAO || ""}
                tagIcon="bookmark"
                photo={teacher.FOTO_URL || ""}
              />
            </Group>
          </Grid>

          <Grid item xs={12}>
            <CollapseGroup title={t("teachers.details.schedule")}>
              {scheduleError && (
                <Box p={2} textAlign="center">
                  <Typography color="textSecondary">
                    {t("teachers.details.errorLoadSchedule")}
                  </Typography>
                </Box>
              )}
              {!scheduleError && scheduleEmpty && !scheduleLoading && (
                <Box p={2} textAlign="center">
                  <Typography color="textSecondary">
                    {t("teachers.details.noSchedule")}
                  </Typography>
                </Box>
              )}
              {calendarOptions && <Calendar options={calendarOptions} />}
            </CollapseGroup>
          </Grid>

          <Grid item xs={12}>
            <CollapseGroup title={t("teachers.details.classes")}>
              {classes.length === 0 ? (
                <Box p={2} textAlign="center">
                  <Typography color="textSecondary">
                    {t("teachers.details.noClasses")}
                  </Typography>
                </Box>
              ) : (
                <ClassesList
                  classes={classes}
                  accessibleClassIds={accessibleClassIds}
                  size="compact"
                />
              )}
            </CollapseGroup>
          </Grid>
        </Grid>
      </div>
    );
  }
}

TeacherDetail.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.objectOf(PropTypes.string)
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(TeacherDetail)));
